import React, { useState } from 'react';
import { readClientNotes, updateClientNotes, useClientNotes } from '../clientNotesStore';
import type { ClientNotes } from '../clientNotesStore';

export type RecruitIdeaPath = 'graduate' | 'experienced';

const pathOf = (notes: ClientNotes | null): RecruitIdeaPath | null => {
  const value = notes ? notes.recruitment.path : null;
  return value === 'graduate' || value === 'experienced' ? value : null;
};

export const readRecruitIdeaPath = (clientId?: string | null): RecruitIdeaPath | null =>
  pathOf(readClientNotes(clientId));

export const writeRecruitIdeaPath = (clientId: string | null | undefined, path: RecruitIdeaPath | null): void => {
  if (!clientId) return;
  void updateClientNotes(clientId, { recruitment: { path } }, notes => {
    const recruitment = { ...notes.recruitment };
    if (path) recruitment.path = path;
    else delete recruitment.path;
    return { ...notes, recruitment };
  });
};

/** The candidate's 招募路線, kept in step with whichever surface last wrote it. */
export const useRecruitIdeaPath = (clientId?: string | null): RecruitIdeaPath | null =>
  pathOf(useClientNotes(clientId));

const COPY = {
  'zh-Hant-HK': {
    title: '招募路線',
    graduate: '🎓 畢業生',
    experienced: '💼 有經驗',
    clear: '清除路線',
    sample: '以下為情境參考，並非此候選人的資料。',
    prompt: '先問',
    show: '展示',
    next: '下一步',
    focus: '選擇面談重點',
    graduateOccupation: '應屆畢業生',
    graduateNetwork: '尚未了解',
    graduateAi: '先了解對方想學甚麼、擔心甚麼，再邀請他認識導師及首 90 天安排。',
    graduateTopics: [
      {
        key: 'start',
        label: '如何開始',
        prompt: '畢業後想先學甚麼？對保險工作最不確定的是甚麼？',
        show: '首 90 天培訓、導師陪同及日常工作示例。',
        next: '邀請參加新人導師交流。'
      },
      {
        key: 'clients',
        label: '客源支援',
        prompt: '最擔心如何開始認識及服務客戶嗎？',
        show: '團隊的客戶開發訓練與陪同方式。',
        next: '安排一次工作體驗或團隊交流。'
      },
      {
        key: 'income',
        label: '收入安排',
        prompt: '起步收入與支出，有哪部分需要先算清楚？',
        show: '實際報酬結構及適用條件。',
        next: '一起核對書面條件及個人預算。'
      }
    ],
    experiencedTopics: [
      {
        key: 'support',
        label: '團隊支援',
        prompt: '目前團隊最欠缺哪一種支援？',
        show: '導師、行政支援與合作方式的具體例子。',
        next: '安排與主管深入交流。'
      },
      {
        key: 'growth',
        label: '發展空間',
        prompt: '未來兩年最想突破甚麼？',
        show: '團隊發展路線、培訓及晉升條件。',
        next: '比較現況與目標發展路線。'
      },
      {
        key: 'transition',
        label: '轉職安排',
        prompt: '轉職時最擔心失去甚麼？',
        show: '轉職時間、入職支援及實際條件。',
        next: '列出轉職清單，逐項核實。'
      }
    ]
  },
  zh: {
    title: '招募路线',
    graduate: '🎓 毕业生',
    experienced: '💼 有经验',
    clear: '清除路线',
    sample: '以下为情境参考，并非此候选人的资料。',
    prompt: '先问',
    show: '展示',
    next: '下一步',
    focus: '选择面谈重点',
    graduateOccupation: '应届毕业生',
    graduateNetwork: '尚未了解',
    graduateAi: '先了解对方想学什么、担心什么，再邀请他认识导师及首 90 天安排。',
    graduateTopics: [
      {
        key: 'start',
        label: '如何开始',
        prompt: '毕业后想先学什么？对保险工作最不确定的是什么？',
        show: '首 90 天培训、导师陪同及日常工作示例。',
        next: '邀请参加新人导师交流。'
      },
      {
        key: 'clients',
        label: '客源支持',
        prompt: '最担心如何开始认识及服务客户吗？',
        show: '团队的客户开发训练与陪同方式。',
        next: '安排一次工作体验或团队交流。'
      },
      {
        key: 'income',
        label: '收入安排',
        prompt: '起步收入与支出，有哪部分需要先算清楚？',
        show: '实际报酬结构及适用条件。',
        next: '一起核对书面条件及个人预算。'
      }
    ],
    experiencedTopics: [
      {
        key: 'support',
        label: '团队支持',
        prompt: '目前团队最欠缺哪一种支持？',
        show: '导师、行政支持与合作方式的具体例子。',
        next: '安排与主管深入交流。'
      },
      {
        key: 'growth',
        label: '发展空间',
        prompt: '未来两年最想突破什么？',
        show: '团队发展路线、培训及晋升条件。',
        next: '比较现况与目标发展路线。'
      },
      {
        key: 'transition',
        label: '转职安排',
        prompt: '转职时最担心失去什么？',
        show: '转职时间、入职支持及实际条件。',
        next: '列出转职清单，逐项核实。'
      }
    ]
  },
  en: {
    title: 'Recruitment path',
    graduate: '🎓 Graduate',
    experienced: '💼 Experienced',
    clear: 'Clear path',
    sample: 'These are example scenarios, not this candidate’s facts.',
    prompt: 'Ask first',
    show: 'Show',
    next: 'Next step',
    focus: 'Choose a conversation focus',
    graduateOccupation: 'Fresh graduate',
    graduateNetwork: 'Not yet known',
    graduateAi: 'Ask what they hope to learn and what concerns them, then introduce a mentor and the first 90 days.',
    graduateTopics: [
      {
        key: 'start',
        label: 'Getting started',
        prompt: 'What do you hope to learn after graduation? What feels uncertain about insurance?',
        show: 'The first 90 days of training, mentor support, and a day-in-the-life example.',
        next: 'Invite them to meet a new-agent mentor.'
      },
      {
        key: 'clients',
        label: 'Finding clients',
        prompt: 'Is meeting and serving your first clients your biggest concern?',
        show: 'How the team trains and accompanies new agents in client development.',
        next: 'Arrange a job shadow or team conversation.'
      },
      {
        key: 'income',
        label: 'Starting income',
        prompt: 'Which parts of starting income and costs should we clarify?',
        show: 'The actual compensation structure and eligibility conditions.',
        next: 'Review the written terms and their budget together.'
      }
    ],
    experiencedTopics: [
      {
        key: 'support',
        label: 'Team support',
        prompt: 'What support is most missing from your current team?',
        show: 'Specific examples of mentoring, administration, and collaboration.',
        next: 'Arrange a deeper discussion with the team leader.'
      },
      {
        key: 'growth',
        label: 'Growth',
        prompt: 'What do you most want to achieve in the next two years?',
        show: 'Development paths, training, and promotion conditions.',
        next: 'Compare their current and desired paths.'
      },
      {
        key: 'transition',
        label: 'Transition',
        prompt: 'What are you most concerned about losing if you move?',
        show: 'The transition timeline, joining support, and actual terms.',
        next: 'Make a transition checklist and verify each item.'
      }
    ]
  }
} as const;

export const recruitIdeaPathCopy = (locale?: string) => COPY[locale as keyof typeof COPY] || COPY.en;

/**
 * 面談重點 for the candidate's 招募路線 — the guide only. The 🎓 / 💼 choice
 * itself is NOT made here any more: it is one select in 快速資料
 * (ClientDetailDrawer's 招募 card), or an answer in the candidate's own quiz, so
 * 招募 IDEA reads a fact instead of asking the adviser to pick a mode on
 * the way in. Nothing renders until that fact exists — an unanswered path used
 * to carry a whole panel of chrome over an empty body.
 */
export const RecruitIdeaPathGuide = ({ path, locale }: { path: RecruitIdeaPath | null; locale?: string }) => {
  const copy = recruitIdeaPathCopy(locale);
  const [focus, setFocus] = useState<string | null>(null);
  const topics = path === 'graduate' ? copy.graduateTopics : path === 'experienced' ? copy.experiencedTopics : [];
  const active = topics.find(topic => topic.key === focus) || topics[0];

  if (!path || !active) return null;

  return (
    <section className="clientv2-idea-path" data-testid="recruit-idea-path">
      <div className="clientv2-idea-path-guide" data-testid="recruit-idea-path-guide">
        <p className="clientv2-idea-path-sample">{copy.sample}</p>
        <span className="clientv2-idea-path-label">
          {copy[path]} · {copy.focus}
        </span>
        <div className="clientv2-idea-path-topics">
          {topics.map(topic => (
            <button
              type="button"
              key={topic.key}
              aria-pressed={active.key === topic.key}
              onClick={() => setFocus(topic.key)}
            >
              {topic.label}
            </button>
          ))}
        </div>
        <dl>
          <div>
            <dt>{copy.prompt}</dt>
            <dd>{active.prompt}</dd>
          </div>
          <div>
            <dt>{copy.show}</dt>
            <dd>{active.show}</dd>
          </div>
          <div>
            <dt>{copy.next}</dt>
            <dd>{active.next}</dd>
          </div>
        </dl>
      </div>
    </section>
  );
};
